import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionGroup,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import { PubkeyRequest } from '../../../types/models/Pubkey';

interface PubkeyFormProps {
  initialValues?: PubkeyRequest;
  onSubmit: (values: PubkeyRequest) => Promise<void>;
  isSubmitting: boolean;
  submitLabel: string;
  validationErrors?: Record<string, string>;
}

const PubkeyForm: React.FunctionComponent<PubkeyFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting,
  submitLabel,
  validationErrors,
}) => {
  const navigate = useNavigate();
  const [label, setLabel] = useState(initialValues?.label ?? '');
  const [content, setContent] = useState(initialValues?.content ?? '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const clientErrors: Record<string, string> = {};
  if (touched.label && !label.trim()) {
    clientErrors.label = 'Label is required';
  } else if (touched.label && label.length > 255) {
    clientErrors.label = 'Label must be 255 characters or fewer';
  }
  if (touched.content && !content.trim()) {
    clientErrors.content = 'SSH public key is required';
  }

  const getFieldError = (field: string): string | undefined => {
    return validationErrors?.[field] || clientErrors[field];
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched({ label: true, content: true });

    if (!label.trim() || label.length > 255 || !content.trim()) {
      return;
    }

    await onSubmit({ label: label.trim(), content: content.trim() });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup label="Label" isRequired fieldId="pubkey-label">
        <TextInput
          id="pubkey-label"
          isRequired
          value={label}
          onChange={(_event, value) => setLabel(value)}
          onBlur={() => setTouched((prev) => ({ ...prev, label: true }))}
          validated={getFieldError('label') ? 'error' : 'default'}
        />
        {getFieldError('label') && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem variant="error">{getFieldError('label')}</HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
      <FormGroup label="SSH Public Key" isRequired fieldId="pubkey-content">
        <TextArea
          id="pubkey-content"
          isRequired
          value={content}
          onChange={(_event, value) => setContent(value)}
          onBlur={() => setTouched((prev) => ({ ...prev, content: true }))}
          validated={getFieldError('content') ? 'error' : 'default'}
          style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}
          rows={6}
        />
        {getFieldError('content') && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem variant="error">{getFieldError('content')}</HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
      <ActionGroup>
        <Button variant="primary" type="submit" isLoading={isSubmitting} isDisabled={isSubmitting}>
          {submitLabel}
        </Button>
        <Button variant="link" onClick={() => navigate('/datacenter/pubkeys')} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ActionGroup>
    </Form>
  );
};

export default PubkeyForm;
